import React, { useState } from "react";

import { Icon } from "@iconify/react/dist/iconify.js";
import { Box, CircularProgress, Modal } from "@mui/material";
import { usePostDeleteSummary } from "api/apiPostDeleteSummary";
import { clsx } from "clsx";
import { AlertColor, TYPE_FOLDER } from "util/constants";
import { DocumentData } from "util/interfaces/DocumentData";

import { Alerts } from "../../Alerts/Alerts";

import { getDocumentName } from "./DeleteModal.Utils";
import { usePostDeleteBlobs } from "@/api/useDeleteBlob";

import styles from "./DeleteModal.module.scss";

interface DeleteModalProps {
  callbackClose: (_update: boolean) => void;
  documents: DocumentData[];
  selectedRows?: DocumentData[];
}

export const DeleteModal: React.FC<DeleteModalProps> = ({
  callbackClose,
  documents,
  selectedRows,
}) => {
  const { mutateAsync: callDeleteSummary } = usePostDeleteSummary();
  const [alertColor, setAlertColor] = useState<AlertColor>(AlertColor.Info);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { mutateAsync: callDeleteBlobs } = usePostDeleteBlobs();

  const deleteSummary = async () => {
    try {
      if (documents.length === 0) {
        throw new Error("There are no documents in array");
      }
      let [documentName] = documents.map((d) => d.fileAlias);
      const response = await callDeleteSummary(getDocumentName(documentName));
      if (response.status != 200) {
        throw new Error("Failed to delete summary");
      }
      return true;
    } catch {
      return false;
    }
  };

  const deleteBlob = async () => {
    const response = await callDeleteBlobs(documents.map((d) => d.fileAlias));
    if (response.status !== 200) {
      throw new Error("Failed to delete blob");
    }
  };

  const deleteDocuments = async () => {
    setIsLoading(true);

    try {
      const summaryDeleted = await deleteSummary();
      if (summaryDeleted) {
        await deleteBlob();
        callbackClose(true);
      } else {
        throw new Error("Failed to delete summary");
      }
    } catch {
      setAlertColor(AlertColor.Error);
      setAlertMessage("Unexpected error while trying to delete files from the storage.");
      setIsLoading(false);
    }
  };

  const getSubmitButtonText = () => {
    if (isLoading) {
      return (
        <div style={{ display: "flex", alignItems: "center", padding: "0 10px" }}>
          <CircularProgress size={15} />
          <span style={{ marginLeft: 10 }}>Deleting...</span>
        </div>
      );
    }
    if (selectedRows?.every((row) => row.id.includes(`${TYPE_FOLDER}-`))) {
      return "Delete folder(s)";
    }

    if (selectedRows?.every((row) => Number.isInteger(Number(row.id)))) {
      return "Delete file(s)";
    }

    return "Delete";
  };

  return (
    <>
      <Modal open={true} onClose={() => callbackClose(false)} data-testid="modalDeleteFiles">
        <div className={clsx(styles.modal, "modal-kb-content")}>
          <Box onClick={() => callbackClose(false)} className={styles.closeIcon}>
            <Icon icon="bitcoin-icons:cross-filled" height={30} color="#000000" />
          </Box>
          <Box display="flex" flexDirection="column" gap={2}>
            <div className="flex gap-5 align-items-center ">
              <Icon icon="ep:warning" style={{ color: "var(--black)", fontSize: "20px" }} />
              <span className={styles.title}>Warning</span>
            </div>
            <p className={styles.description}>
              This action will delete all files contained within the selected item(s).
            </p>
          </Box>
          <div className="modal-kb-footer">
            <button
              className="save_btn"
              onClick={deleteDocuments}
              disabled={isLoading}
              data-testid="deleteModal-deleteButton"
            >
              {getSubmitButtonText()}
            </button>
            <button
              className="cancel_btn"
              onClick={() => callbackClose(false)}
              data-testid="deleteModal-cancelButton"
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
      <Alerts
        style={{ top: "90%", right: "unset", left: "unset" }}
        alertOpen={alertMessage !== ""}
        color={alertColor}
        message={alertMessage}
        handleAlertClose={() => setAlertMessage("")}
      />
    </>
  );
};
